import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../../../shared/context/AppContext';
import { useAnalytics } from '../../../../shared/hooks/useAnalytics';
import { EVENTS } from '../../../../shared/services/analyticsEvents';
import { ALLOWED_SIGNUP_DOMAINS, orgIdFromEmail } from '../../../../shared/firebase';
import {
  register,
  login,
  resendVerification,
  authErrorMessage,
  logout,
} from '../../../../shared/services/authService';
import ColourTextLogo from '../../../../colour_text_logo.svg';
import MakerspaceDarkLogo from '../../../../makerspace_dark_logo.png';
import './LoginScreen.css';

const LoginScreen = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppContext();
  const { trackEvent } = useAnalytics();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState(null);

  const isEmailDomainAllowed = (value) =>
    ALLOWED_SIGNUP_DOMAINS.includes(orgIdFromEmail(value?.toLowerCase().trim()));

  // Password complexity validation
  const validatePasswordComplexity = (pwd) => {
    if (pwd.length < 8) {
      return { valid: false, error: 'Password must be at least 8 characters long' };
    }
    if (!/[A-Z]/.test(pwd)) {
      return { valid: false, error: 'Password must contain at least one uppercase letter' };
    }
    if (!/[a-z]/.test(pwd)) {
      return { valid: false, error: 'Password must contain at least one lowercase letter' };
    }
    if (!/[0-9]/.test(pwd)) {
      return { valid: false, error: 'Password must contain at least one number' };
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) {
      return { valid: false, error: 'Password must contain at least one special character' };
    }
    return { valid: true };
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  const handleResendVerification = async () => {
    setLoading(true);
    setError(null);

    try {
      await resendVerification();
      setSuccessMessage('Verification email sent. Please check your inbox.');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Resend verification error:', err);
      setError(authErrorMessage(err, 'Failed to resend verification email'));
    } finally {
      setLoading(false);
    }
  };

  const showUnverifiedError = () => {
    setError(
      <span>
        Please verify your email before logging in.
        {' '}
        <button
          type="button"
          onClick={handleResendVerification}
          className="toggle-mode-button"
          style={{
            display: 'inline',
            padding: 0,
            margin: 0,
            marginLeft: '4px',
          }}
        >
          Resend verification email
        </button>
      </span>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const normalizedEmail = email.toLowerCase().trim();

      if (isRegisterMode) {
        if (!isEmailDomainAllowed(normalizedEmail)) {
          setError('Please use an email address from @defence.gov.au');
          setLoading(false);
          return;
        }

        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }

        const passwordValidation = validatePasswordComplexity(password);
        if (!passwordValidation.valid) {
          setError(passwordValidation.error);
          setLoading(false);
          return;
        }

        const fbUser = await register(
          normalizedEmail,
          password,
          normalizedEmail.split('@')[0]
        );

        trackEvent(EVENTS.USER_REGISTERED, {
          userId: fbUser.uid,
          orgId: orgIdFromEmail(normalizedEmail),
        });

        // Keep the user signed out until they verify; the auth listener
        // ignores unverified sessions but signing out keeps things tidy.
        await logout();

        setSuccessMessage(
          'Registration successful! Please check your email to verify your account. ' +
          'Once verified, you can sign in below.'
        );

        setEmail(normalizedEmail);
        setPassword('');
        setConfirmPassword('');
        setError(null);

        setTimeout(() => {
          setIsRegisterMode(false);
        }, 3000);
      } else {
        const { user: fbUser, emailVerified } = await login(normalizedEmail, password);

        if (!emailVerified) {
          // Session stays in Firebase Auth (needed for resend) but AppContext
          // will not authenticate it.
          showUnverifiedError();
          setLoading(false);
          return;
        }

        trackEvent(EVENTS.USER_LOGGED_IN, {
          userId: fbUser.uid,
          orgId: orgIdFromEmail(normalizedEmail),
        });

        // Navigation happens via the isAuthenticated effect once AppContext
        // finishes loading the profile.
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(authErrorMessage(err, 'An error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-note-card">
        <p>
          Framework is accessible on the DPE (Defence Protected Environment) for ADF members.
        </p>
        <p>
          Framework is delivered by the MakerSpace program. For enquiries, contact{' '}
          <a href="mailto:armymaker@qinetiq.com.au">armymaker@qinetiq.com.au</a>.
        </p>
      </div>
      <div className="login-container">
        <div className="login-gradient-header"></div>
        <div className="login-card-content">
          <div className="login-logos">
            <img src={ColourTextLogo} alt="Framework" className="login-logo" />
            <span className="login-logo-divider">|</span>
            <img src={MakerspaceDarkLogo} alt="MakerSpace" className="login-logo login-logo-makerspace" />
          </div>

          {error && (
            <div className="login-error" role="alert">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="login-success" role="alert">
              {successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Defence email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your defence email"
                required
                autoComplete="email"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isRegisterMode ? 'At least 8 characters' : 'Your password'}
                required
                autoComplete={isRegisterMode ? 'new-password' : 'current-password'}
                minLength={isRegisterMode ? 8 : undefined}
                disabled={loading}
              />
            </div>

            {isRegisterMode && (
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  autoComplete="new-password"
                  minLength={8}
                  disabled={loading}
                />
              </div>
            )}

            <button
              type="submit"
              className="login-button"
              disabled={loading || (isRegisterMode && !isEmailDomainAllowed(email))}
            >
              {loading ? 'Please wait...' : isRegisterMode ? 'Create Account' : 'Sign In'}
            </button>
          </form>

          <div className="login-footer">
            {!isRegisterMode && (
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
              >
                Forgot password?
              </button>
            )}
            <button
              type="button"
              className="toggle-mode-button"
              onClick={() => {
                setIsRegisterMode(!isRegisterMode);
                setError(null);
                setSuccessMessage(null);
                setPassword('');
                setConfirmPassword('');
              }}
              disabled={loading}
            >
              {isRegisterMode
                ? 'Already have an account? Sign in'
                : "Don't have an account? Register"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
